import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  LogOut,
  Camera,
  ArrowLeft,
  Clock,
  LogIn,
} from "lucide-react";
import Logo from "../components/Logo";

const STEPS = {
  CHOOSE: "choose",
  CHECKIN_SELFIE: "checkin_selfie",
  CHECKOUT_SELFIE: "checkout_selfie",
  DONE: "done",
};

function fmtIST(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

export default function WorkerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(STEPS.CHOOSE);
  const [coords, setCoords] = useState(null);
  const [today, setToday] = useState(null);
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setCoords({ lat: 0, lng: 0 }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  const refreshToday = async () => {
    const r = await api.get("/worker/attendance/today");
    setToday(r.data);
    return r.data;
  };

  useEffect(() => {
    refreshToday();
  }, []);

  const stopCamera = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
  };

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch {
      toast.error("Cannot access camera. Please grant permission.");
    }
  };

  useEffect(() => {
    if (step === STEPS.CHECKIN_SELFIE || step === STEPS.CHECKOUT_SELFIE)
      startCamera();
    return () => stopCamera();
    // eslint-disable-next-line
  }, [step]);

  const submitMark = async (status, selfieData = "") => {
    setLoading(true);
    try {
      await api.post("/worker/attendance", {
        gps_lat: coords?.lat ?? 0,
        gps_lng: coords?.lng ?? 0,
        status,
        selfie_base64: selfieData,
      });
      toast.success(
        status === "present"
          ? "Checked in! Don't forget to check out when leaving."
          : "Marked absent for today"
      );
      await refreshToday();
      setStep(STEPS.DONE);
      stopCamera();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitCheckout = async (selfieData) => {
    setLoading(true);
    try {
      const r = await api.post("/worker/checkout", {
        gps_lat: coords?.lat ?? 0,
        gps_lng: coords?.lng ?? 0,
        status: "present",
        selfie_base64: selfieData,
      });
      toast.success(`Checked out! Total hours: ${r.data.working_hours}`);
      await refreshToday();
      setStep(STEPS.DONE);
      stopCamera();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const captureSelfie = async (kind) => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) {
      toast.error("Camera not ready");
      return;
    }
    const w = 480;
    const h = (v.videoHeight / v.videoWidth) * w;
    c.width = w;
    c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    const data = c.toDataURL("image/jpeg", 0.6);
    stopCamera();
    if (kind === "in") {
      await submitMark("present", data);
    } else {
      await submitCheckout(data);
    }
  };

  const handleLogout = () => {
    stopCamera();
    logout();
    nav("/");
  };

  // Project context for GPS pre-check
  const project = user?.project;

  // Pre-validate GPS before opening camera
  const startCheckIn = () => {
    if (!project) {
      toast.error("No project assigned. Contact admin.");
      return;
    }
    if (project.gps_enforcement_enabled !== false) {
      if (!coords || (coords.lat === 0 && coords.lng === 0)) {
        toast.error("Could not get your location. Please enable GPS and try again.");
        return;
      }
      // Haversine distance
      const R = 6371000;
      const toRad = (d) => (d * Math.PI) / 180;
      const dLat = toRad(project.site_lat - coords.lat);
      const dLng = toRad(project.site_lng - coords.lng);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(coords.lat)) *
          Math.cos(toRad(project.site_lat)) *
          Math.sin(dLng / 2) ** 2;
      const distance = 2 * R * Math.asin(Math.sqrt(a));
      if (distance > (project.gps_radius_meters || 200)) {
        toast.error("You are outside the office premises. Cannot mark present.");
        return;
      }
    }
    setStep(STEPS.CHECKIN_SELFIE);
  };

  const startCheckOut = () => {
    if (project && project.gps_enforcement_enabled !== false) {
      if (!coords || (coords.lat === 0 && coords.lng === 0)) {
        toast.error("Could not get your location. Please enable GPS and try again.");
        return;
      }
      const R = 6371000;
      const toRad = (d) => (d * Math.PI) / 180;
      const dLat = toRad(project.site_lat - coords.lat);
      const dLng = toRad(project.site_lng - coords.lng);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(coords.lat)) *
          Math.cos(toRad(project.site_lat)) *
          Math.sin(dLng / 2) ** 2;
      const distance = 2 * R * Math.asin(Math.sqrt(a));
      if (distance > (project.gps_radius_meters || 200)) {
        toast.error("You are outside the office premises. Cannot check out.");
        return;
      }
    }
    setStep(STEPS.CHECKOUT_SELFIE);
  };

  const rec = today?.record;
  const checkedIn = rec?.status === "present" && rec?.check_in_at;
  const checkedOut = !!rec?.check_out_at;
  const isAbsent = rec?.status === "absent";

  // Derive current sensible step
  useEffect(() => {
    if (!today) return;
    if (!today.marked) setStep(STEPS.CHOOSE);
    else if (isAbsent) setStep(STEPS.DONE);
    else if (checkedIn && !checkedOut) setStep(STEPS.CHOOSE);
    else if (checkedOut) setStep(STEPS.DONE);
    // eslint-disable-next-line
  }, [today]);

  const isSelfieStep =
    step === STEPS.CHECKIN_SELFIE || step === STEPS.CHECKOUT_SELFIE;

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isSelfieStep ? (
            <button
              onClick={() => {
                stopCamera();
                setStep(STEPS.CHOOSE);
              }}
              data-testid="back-to-choose"
              className="text-[#1A3A5C] hover:bg-slate-100 p-2 rounded-full inline-flex items-center"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <Logo size={36} showTagline={false} />
          )}
          <div>
            <div className="font-display font-semibold text-sm text-[#1A3A5C]">
              ITCOT
            </div>
            <div className="text-[10px] text-[#475569]">Employee Portal</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="worker-logout"
          className="text-sm text-[#475569] hover:text-red-600 inline-flex items-center gap-1"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Welcome (hide during selfie steps) */}
        {!isSelfieStep && (
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wider text-[#475569] font-semibold">
              Welcome
            </div>
            <h2 className="font-display font-bold text-2xl text-[#1A3A5C]">
              {user?.name}
            </h2>
            <div className="text-sm text-[#475569] font-mono mt-0.5">
              {user?.worker_id}
            </div>
          </Card>
        )}

        {/* DONE state */}
        {step === STEPS.DONE && rec && (
          <Card data-testid="attendance-done-card" className="p-6 text-center">
            {isAbsent ? (
              <>
                <XCircle className="w-14 h-14 text-slate-400 mx-auto" />
                <h3 className="font-display font-bold text-2xl text-[#1A3A5C] mt-3">
                  Marked Absent
                </h3>
                <p className="text-sm text-[#475569] mt-1">See you tomorrow.</p>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
                <h3 className="font-display font-bold text-2xl text-[#1A3A5C] mt-3">
                  Day Complete
                </h3>
                <div className="mt-4 bg-slate-50 rounded-lg p-3 text-left text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-[#475569]">Check-in</span>
                    <span className="font-semibold text-[#1A3A5C]">
                      {fmtIST(rec.check_in_at)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#475569]">Check-out</span>
                    <span className="font-semibold text-[#1A3A5C]">
                      {fmtIST(rec.check_out_at)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-1 mt-1">
                    <span className="text-[#475569]">Total hours</span>
                    <span className="font-bold text-[#22C55E]">
                      {rec.working_hours ?? "—"} hrs
                    </span>
                  </div>
                </div>
              </>
            )}
          </Card>
        )}

        {/* Not marked → Present / Absent */}
        {step === STEPS.CHOOSE && today && !today.marked && (
          <Card className="p-6" data-testid="mark-card">
            <h3 className="font-display font-bold text-xl text-[#1A3A5C] text-center mb-5">
              Mark your attendance
            </h3>
            <div className="space-y-3">
              <Button
                data-testid="mark-present-btn"
                onClick={startCheckIn}
                disabled={loading}
                className="w-full h-16 text-lg bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold rounded-xl shadow-lg active:scale-95 transition-transform"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" /> PRESENT
              </Button>
              <Button
                data-testid="mark-absent-btn"
                onClick={() => submitMark("absent", "")}
                disabled={loading}
                className="w-full h-16 text-lg bg-[#9CA3AF] hover:bg-[#6B7280] text-white font-bold rounded-xl shadow-lg active:scale-95 transition-transform"
              >
                <XCircle className="w-5 h-5 mr-2" /> ABSENT
              </Button>
            </div>
          </Card>
        )}

        {/* Checked in, awaiting check-out */}
        {step === STEPS.CHOOSE && checkedIn && !checkedOut && (
          <Card className="p-6" data-testid="checkout-prompt">
            <div className="text-center">
              <Clock className="w-10 h-10 text-[#2A8A8F] mx-auto mb-2" />
              <h3 className="font-display font-bold text-xl text-[#1A3A5C]">
                You're checked in
              </h3>
              <p className="text-sm text-[#475569] mt-1">
                Checked in at <strong>{fmtIST(rec.check_in_at)}</strong>
              </p>
            </div>
            <Button
              data-testid="checkout-btn"
              onClick={startCheckOut}
              disabled={loading}
              className="w-full mt-5 h-14 bg-[#1A3A5C] hover:bg-[#0F2236] text-white font-bold rounded-xl shadow"
            >
              <LogIn className="w-5 h-5 mr-2 rotate-180" /> Check Out
            </Button>
            <p className="text-xs text-[#475569] mt-2 text-center">
              You'll be asked to take one more selfie.
            </p>
          </Card>
        )}

        {/* Selfie capture (check-in or check-out) */}
        {isSelfieStep && (
          <Card className="p-6" data-testid="selfie-card">
            <h3 className="font-display font-bold text-xl text-[#1A3A5C] text-center mb-3">
              {step === STEPS.CHECKIN_SELFIE
                ? "Check-in Selfie"
                : "Check-out Selfie"}
            </h3>
            <div className="relative bg-black rounded-xl overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full"
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <Button
              data-testid="capture-selfie-btn"
              onClick={() =>
                captureSelfie(step === STEPS.CHECKIN_SELFIE ? "in" : "out")
              }
              disabled={loading}
              className={`w-full mt-4 h-14 text-white font-bold rounded-xl shadow ${
                step === STEPS.CHECKIN_SELFIE
                  ? "bg-[#22C55E] hover:bg-[#16A34A]"
                  : "bg-[#1A3A5C] hover:bg-[#0F2236]"
              }`}
            >
              <Camera className="w-5 h-5 mr-2" />
              {loading
                ? "Submitting..."
                : step === STEPS.CHECKIN_SELFIE
                ? "Capture & Check In"
                : "Capture & Check Out"}
            </Button>
          </Card>
        )}
      </main>
    </div>
  );
}
